import React, { useState, useEffect } from 'react';
import { api, LambdaFunctionStatus } from '../utils/api';
import { config, debugLog } from '../utils/config';

interface LambdaFunction {
  name: string;
  status: 'healthy' | 'warning' | 'error';
  lastInvocation: Date;
  duration: number;
  memoryUsed: number;
  errorRate: number;
  invocations24h: number;
}

interface ServiceMetric {
  name: string;
  value: string;
  change: string;
  trend: 'up' | 'down' | 'stable';
  status: 'good' | 'warning' | 'critical';
}

export const OffChainMonitor: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [lambdaFunctions, setLambdaFunctions] = useState<LambdaFunctionStatus[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Service metrics will be populated from real API data only
  const [serviceMetrics, setServiceMetrics] = useState<ServiceMetric[]>([]);

  // Infrastructure status data
  const [dynamoDBTables, setDynamoDBTables] = useState<any[]>([]);
  const [awsServices, setAWSServices] = useState<any[]>([]);

  // Store yesterday's metrics for comparison
  const [yesterdayMetrics, setYesterdayMetrics] = useState<{
    invocations: number;
    duration: number;
    errorRate: number;
    memory: number;
  } | null>(null);

  // Calculate service metrics from Lambda function data
  useEffect(() => {
    if (lambdaFunctions.length > 0) {
      const totalInvocations = lambdaFunctions.reduce((sum, f) => sum + f.invocations24h, 0);
      const avgDuration = lambdaFunctions.reduce((sum, f) => sum + f.duration, 0) / lambdaFunctions.length;
      const avgErrorRate = lambdaFunctions.reduce((sum, f) => sum + f.errorRate, 0) / lambdaFunctions.length;
      const avgMemory = lambdaFunctions.reduce((sum, f) => sum + f.memoryUsed, 0) / lambdaFunctions.length;

      // Calculate changes from yesterday
      const calculateChange = (current: number, yesterday: number | undefined): { change: string; trend: 'up' | 'down' | 'stable' } => {
        if (yesterday === undefined || yesterday === 0) {
          return { change: 'N/A', trend: 'stable' };
        }
        const diff = current - yesterday;
        const percentChange = (diff / yesterday) * 100;

        if (Math.abs(percentChange) < 1) {
          return { change: '±0%', trend: 'stable' };
        }

        const sign = diff > 0 ? '+' : '';
        return {
          change: `${sign}${percentChange.toFixed(1)}%`,
          trend: diff > 0 ? 'up' : 'down'
        };
      };

      const invocationsChange = calculateChange(totalInvocations, yesterdayMetrics?.invocations);
      const durationChange = calculateChange(avgDuration, yesterdayMetrics?.duration);
      const errorRateChange = calculateChange(avgErrorRate, yesterdayMetrics?.errorRate);
      const memoryChange = calculateChange(avgMemory, yesterdayMetrics?.memory);

      setServiceMetrics([
        {
          name: 'Total Invocations',
          value: totalInvocations.toLocaleString(),
          change: invocationsChange.change,
          trend: invocationsChange.trend,
          status: 'good'
        },
        {
          name: 'Average Duration',
          value: `${(avgDuration / 1000).toFixed(1)}s`,
          change: durationChange.change,
          trend: durationChange.trend,
          status: avgDuration > 3000 ? 'warning' : 'good'
        },
        {
          name: 'Error Rate',
          value: `${avgErrorRate.toFixed(1)}%`,
          change: errorRateChange.change,
          trend: errorRateChange.trend,
          status: avgErrorRate > 1 ? 'warning' : 'good'
        },
        {
          name: 'Memory Utilization',
          value: `${Math.round((avgMemory / 256) * 100)}%`,
          change: memoryChange.change,
          trend: memoryChange.trend,
          status: avgMemory > 200 ? 'warning' : 'good'
        }
      ]);

      // Store current metrics as yesterday's after 24 hours
      // In production, this would be stored in localStorage with timestamp
      if (!yesterdayMetrics) {
        const stored = localStorage.getItem('bws-yesterday-metrics');
        if (stored) {
          const parsed = JSON.parse(stored);
          const age = Date.now() - parsed.timestamp;
          // Only use if less than 48 hours old
          if (age < 48 * 60 * 60 * 1000) {
            setYesterdayMetrics(parsed.data);
          }
        }
      }

      // Update stored metrics every hour
      const shouldUpdate = (() => {
        const stored = localStorage.getItem('bws-yesterday-metrics');
        if (!stored) return true;
        const parsed = JSON.parse(stored);
        const age = Date.now() - parsed.timestamp;
        return age > 60 * 60 * 1000; // 1 hour
      })();

      if (shouldUpdate) {
        localStorage.setItem('bws-yesterday-metrics', JSON.stringify({
          timestamp: Date.now(),
          data: {
            invocations: totalInvocations,
            duration: avgDuration,
            errorRate: avgErrorRate,
            memory: avgMemory
          }
        }));
      }
    } else {
      setServiceMetrics([]);
    }
  }, [lambdaFunctions, yesterdayMetrics]);

  const categories = [
    { id: 'all', label: 'All Services' },
    { id: 'trading', label: 'Trading Engine' },
    { id: 'protection', label: 'Anti-Manipulation' },
    { id: 'data', label: 'Data Processing' },
    { id: 'monitoring', label: 'Monitoring' }
  ];

  // Load Lambda function data
  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Test LocalStack connection if enabled
      if (config.localstack.enabled) {
        const connectionTest = await api.testLocalStack();
        if (!connectionTest.connected) {
          debugLog('LocalStack connection failed:', connectionTest.error);
          setError(`LocalStack connection failed: ${connectionTest.error}`);
          setLambdaFunctions([]); // Never use mock data
          setDynamoDBTables([]);
          setAWSServices([]);
          return;
        }
        debugLog('LocalStack connection successful');
      }

      // Load all monitoring data in parallel
      const [functions, tables, services] = await Promise.allSettled([
        api.lambda.getFunctionsStatus(),
        api.lambda.getDynamoDBTablesStatus(),
        api.lambda.getAWSServicesStatus()
      ]);

      // Handle Lambda functions
      if (functions.status === 'fulfilled') {
        setLambdaFunctions(functions.value);
      } else {
        debugLog('Lambda functions fetch failed:', functions.reason);
        setLambdaFunctions([]);
      }

      // Handle DynamoDB tables
      if (tables.status === 'fulfilled') {
        setDynamoDBTables(tables.value);
      } else {
        debugLog('DynamoDB tables fetch failed:', tables.reason);
        setDynamoDBTables([]);
      }

      // Handle AWS services
      if (services.status === 'fulfilled') {
        setAWSServices(services.value);
      } else {
        debugLog('AWS services fetch failed:', services.reason);
        setAWSServices([]);
      }

      setLastUpdate(new Date());

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load monitoring data';
      debugLog('Monitoring data loading error:', errorMessage);
      setError(errorMessage);
      setLambdaFunctions([]); // Never use mock data
      setDynamoDBTables([]);
      setAWSServices([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRefresh = async () => {
    await loadData();
  };
  
  // Initial data load and periodic refresh
  useEffect(() => {
    loadData();
    
    const interval = setInterval(loadData, config.intervals.medium);
    
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status: LambdaFunctionStatus['status']) => {
    switch (status) {
      case 'healthy':
        return 'bi-check-circle-fill';
      case 'warning':
        return 'bi-exclamation-triangle-fill';
      case 'error':
        return 'bi-exclamation-triangle-fill';
      default:
        return 'bi-check-circle-fill';
    }
  };

  const getStatusColor = (status: LambdaFunctionStatus['status']) => {
    switch (status) {
      case 'healthy':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      case 'error':
        return 'text-danger';
      default:
        return 'text-secondary';
    }
  };

  const getMetricStatusColor = (status: ServiceMetric['status']) => {
    switch (status) {
      case 'good':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      case 'critical':
        return 'text-danger';
      default:
        return 'text-secondary';
    }
  };

  const getCloudWatchLogsUrl = (functionName: string): string => {
    const region = 'us-east-1';
    const logGroup = `/aws/lambda/bws-token-market-maker-${functionName}-prod`;
    const encodedLogGroup = encodeURIComponent(logGroup);

    return `https://console.aws.amazon.com/cloudwatch/home?region=${region}#logsV2:log-groups/log-group/${encodedLogGroup}`;
  };

  const filteredFunctions = selectedCategory === 'all' 
    ? lambdaFunctions 
    : lambdaFunctions.filter(fn => {
        switch (selectedCategory) {
          case 'trading':
            return fn.name.includes('trading') || fn.name.includes('strategy');
          case 'protection':
            return fn.name.includes('pattern') || fn.name.includes('mev') || fn.name.includes('protection');
          case 'data':
            return fn.name.includes('data') || fn.name.includes('aggregator');
          case 'monitoring':
            return fn.name.includes('monitor') || fn.name.includes('risk');
          default:
            return true;
        }
      });

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h1 className="h2 mb-2">Off-Chain Monitor</h1>
          <p className="text-muted">
            AWS Lambda functions and serverless infrastructure monitoring
            {config.localstack.enabled && (
              <span className="ms-2 badge bg-primary-subtle text-primary border border-primary">
                <i className="bi bi-hdd-network me-1"></i>
                LocalStack Mode
              </span>
            )}
          </p>
        </div>

        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className="btn btn-outline-primary d-flex align-items-center gap-2"
        >
          <i className={`bi bi-arrow-clockwise ${isLoading ? 'spinner-border spinner-border-sm' : ''}`}></i>
          Refresh
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className={`alert ${
          error.includes('not yet available') || error.includes('coming soon')
            ? 'alert-info'
            : 'alert-danger'
        } d-flex align-items-start mb-4`} role="alert">
          <i className={`bi ${
            error.includes('not yet available') || error.includes('coming soon')
              ? 'bi-info-circle-fill'
              : 'bi-exclamation-triangle-fill'
          } me-3 fs-4`}></i>
          <div className="flex-grow-1">
            <h5 className="alert-heading mb-1">
              {error.includes('not yet available') || error.includes('coming soon')
                ? 'Feature In Development'
                : 'Connection Error'}
            </h5>
            <p className="mb-0">{error}</p>
          </div>
        </div>
      )}

      {/* Service Metrics */}
      <div className="row g-3 mb-4">
        {serviceMetrics.map((metric, index) => (
          <div key={index} className="col-12 col-sm-6 col-lg-3">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <p className="small fw-medium text-muted mb-1">{metric.name}</p>
                    <p className="h3 fw-bold mb-1">{metric.value}</p>
                    <p className={`small mb-0 ${getMetricStatusColor(metric.status)}`}>
                      {metric.change} vs yesterday
                    </p>
                  </div>
                  <div className="p-2 rounded-circle bg-secondary-subtle">
                    <i className="bi bi-activity fs-4 text-secondary"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Category Filter */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="d-flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`btn btn-sm ${
                  selectedCategory === category.id
                    ? 'btn-primary'
                    : 'btn-outline-secondary'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Lambda Functions */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-transparent">
          <h2 className="h5 fw-semibold mb-1">Lambda Functions</h2>
          <p className="small text-muted mb-0">
            {filteredFunctions.length} of {lambdaFunctions.length} functions shown
          </p>
        </div>
        <div className="card-body">
          <div className="row g-3">
            {filteredFunctions.map((func, index) => {
              const statusIcon = getStatusIcon(func.status);

              return (
                <div key={index} className="col-12 col-lg-6">
                  <div className="card border h-100">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h3 className="h6 fw-semibold mb-1">{func.name}</h3>
                          <p className="small text-muted mb-0">
                            Last invoked {Math.floor((Date.now() - new Date(func.lastInvocation).getTime()) / 60000)}m ago
                          </p>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <i className={`bi ${statusIcon} ${getStatusColor(func.status)}`}></i>
                          <span className={`badge ${getStatusColor(func.status).replace('text-', 'bg-')}`}>
                            {func.status.charAt(0).toUpperCase() + func.status.slice(1)}
                          </span>
                        </div>
                      </div>

                      <div className="row g-3 small mb-3">
                        <div className="col-6">
                          <p className="text-muted mb-1">Duration</p>
                          <p className="fw-medium mb-0">{func.duration}ms</p>
                        </div>
                        <div className="col-6">
                          <p className="text-muted mb-1">Memory</p>
                          <p className="fw-medium mb-0">{func.memoryUsed}MB</p>
                        </div>
                        <div className="col-6">
                          <p className="text-muted mb-1">Error Rate</p>
                          <p className={`fw-medium mb-0 ${func.errorRate > 1 ? 'text-danger' : 'text-success'}`}>
                            {func.errorRate}%
                          </p>
                        </div>
                        <div className="col-6">
                          <p className="text-muted mb-1">Invocations (24h)</p>
                          <p className="fw-medium mb-0">{func.invocations24h.toLocaleString()}</p>
                        </div>
                      </div>

                      <div className="pt-3 border-top">
                        <a
                          href={getCloudWatchLogsUrl(func.name)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-link btn-sm p-0 text-primary text-decoration-none"
                        >
                          View CloudWatch logs <i className="bi bi-arrow-right"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Infrastructure Status */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-md-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-transparent">
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-database fs-5 text-primary"></i>
                <h3 className="h6 fw-semibold mb-0">DynamoDB Tables</h3>
              </div>
            </div>
            <div className="card-body">
              {dynamoDBTables.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <p className="mb-0">No table data available</p>
                </div>
              ) : (
                dynamoDBTables.map((table, index) => (
                  <div
                    key={index}
                    className={`d-flex justify-content-between align-items-center ${index < dynamoDBTables.length - 1 ? 'mb-3' : ''}`}
                  >
                    <span className="text-muted">{table.tableName}</span>
                    <span className={`badge bg-${table.status === 'Active' ? 'success' : 'warning'}`}>
                      {table.status}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-transparent">
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-gear fs-5 text-info"></i>
                <h3 className="h6 fw-semibold mb-0">AWS Services</h3>
              </div>
            </div>
            <div className="card-body">
              {awsServices.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <p className="mb-0">No services data available</p>
                </div>
              ) : (
                awsServices.map((service, index) => (
                  <div
                    key={index}
                    className={`d-flex justify-content-between align-items-center ${index < awsServices.length - 1 ? 'mb-3' : ''}`}
                  >
                    <span className="text-muted">{service.name}</span>
                    <span className={`badge bg-${
                      service.status === 'Operational' ? 'success' :
                      service.status === 'Degraded' ? 'warning' :
                      'danger'
                    }`}>
                      {service.status}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};